import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    MdDashboard,
    MdAdminPanelSettings,
    MdPeople,
    MdHistory,
    MdScience,
    MdMenu,
    MdLogout,
    MdExpandMore,
    MdChevronRight
} from 'react-icons/md';
import { HiDocumentDownload } from 'react-icons/hi';
import styles from './SideBar.module.css';
import { removeAuthToken } from '../../../Config/api';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';

const Sidebar = ({ isOpen, onToggle }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        cro: true,
        pi: true,
        sp: true
    });

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            const parsedUser = JSON.parse(userData);
            console.log('👤 User Data in Sidebar:', parsedUser);
            console.log('🎭 User Role:', parsedUser.role);
            console.log('🎫 Role Name:', parsedUser.role?.roleName);
            setUser(parsedUser);
        }
    }, []);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    // const handleLogoutConfirm = () => {
    //     removeAuthToken();
    //     localStorage.removeItem('user');
    //     setShowLogoutModal(false);
    //     navigate('/login', { replace: true });
    // };

    const handleLogoutConfirm = async () => {
        try {
            // 1. Call backend logout API (BEFORE clearing token!)
            const token = localStorage.getItem('token'); // or however you store it

            const response = await fetch('https://demostudy.onrender.com/api/auth/logout', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                console.log(' Logout recorded in database');
            }
        } catch (error) {
            console.error(' Logout API call failed:', error);
            // Continue with logout even if API fails (optional)
        } finally {
            // 2. Clear frontend data (happens regardless of API success)
            removeAuthToken();
            localStorage.removeItem('user');
            setShowLogoutModal(false);

            // 3. Redirect to login
            navigate('/login', { replace: true });
        }
    };

    const handleLogoutCancel = () => {
        setShowLogoutModal(false);
    };

    // CRO Menu Items (Only for CRO Admin)
    const croMenuItems = [
        { icon: MdDashboard, label: 'Dashboard', path: '/' },
        { icon: MdAdminPanelSettings, label: 'Role Management', path: '/role-management' },
        { icon: MdPeople, label: 'User Management', path: '/user-management' },
        { icon: MdHistory, label: 'Audit Trail', path: '/audit-trail' },
        { icon: HiDocumentDownload, label: 'Report Studio', path: '/study-report' }

    ];

    // PI/Doctor Menu Items (Only for PI users)
    const piMenuItems = [
        { icon: MdScience, label: 'Study Management', path: '/study-management' }
    ];

    const sponsorMenuItems = [
        { icon: MdDashboard, label: 'Dashboard', path: '/' },
        { icon: HiDocumentDownload, label: 'Report Studio', path: '/study-report' }
    ]

    //   FIXED: Check user.role.roleName instead of user.role
    const userRoleName = user?.role?.roleName;
    const isCROAdmin = userRoleName === 'CRO Admin';
    const isPIUser = userRoleName === 'Principal Investigator' || userRoleName === 'Co-ordinator member';
    const isSponsor = userRoleName === "Sponsor";
    console.log('Sidebar Role Check:', {
        userRoleName,
        isCROAdmin,
        isPIUser
    });

    return (
        <>
            <div className={`${styles.sidebar} ${isOpen ? styles.open : styles.closed}`}>
                {/* Logo Section */}
                <div className={styles.logoSection}>
                    <div className={styles.logoIcon}>
                        <div className={styles.iconBox}>
                            {isCROAdmin && 'CRO'}
                            {isSponsor && "SP"}
                            {isPIUser && "PI"}
                        </div>
                    </div>
                    {isOpen && (
                        <span className={styles.logoText}>
                            {isCROAdmin && 'CRO Portal'}
                            {isSponsor && "Sponsor Portal"}
                            {isPIUser && "PI Portal"}
                        </span>
                    )}
                </div>

                {/* Navigation */}
                <nav className={styles.nav}>
                    {/* CRO Admin Menu */}
                    {isCROAdmin && (
                        <div className={styles.navSection}>
                            {isOpen && (
                                <button
                                    className={styles.sectionHeader}
                                    onClick={() => toggleSection('cro')}
                                >
                                    <span className={styles.sectionLabel}>CRO Menu</span>
                                    {expandedSections.cro ? <MdExpandMore /> : <MdChevronRight />}
                                </button>
                            )}
                            {(expandedSections.cro || !isOpen) && croMenuItems.map((item, index) => (
                                <Link
                                    key={index}
                                    to={item.path}
                                    className={`${styles.navItem} ${location.pathname === item.path ? styles.active : ''}`}
                                    title={!isOpen ? item.label : ''}
                                >
                                    <item.icon className={styles.navIcon} />
                                    {isOpen && <span className={styles.navLabel}>{item.label}</span>}
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* PI/Doctor Menu */}
                    {isPIUser && (
                        <div className={styles.navSection}>
                            {isOpen && (
                                <button
                                    className={styles.sectionHeader}
                                    onClick={() => toggleSection('pi')}
                                >
                                    <span className={styles.sectionLabel}>PI/Doctors Menu</span>
                                    {expandedSections.pi ? <MdExpandMore /> : <MdChevronRight />}
                                </button>
                            )}
                            {(expandedSections.pi || !isOpen) && piMenuItems.map((item, index) => (
                                <Link
                                    key={index}
                                    to={item.path}
                                    className={`${styles.navItem} ${location.pathname === item.path ? styles.active : ''}`}
                                    title={!isOpen ? item.label : ''}
                                >
                                    <item.icon className={styles.navIcon} />
                                    {isOpen && <span className={styles.navLabel}>{item.label}</span>}
                                </Link>
                            ))}
                        </div>
                    )}

                    {isSponsor && (
                        <div className={styles.navSection}>
                            {isOpen && (
                                <button
                                    className={styles.sectionHeader}
                                    onClick={() => toggleSection('sp')}
                                >
                                    <span className={styles.sectionLabel}>Sponsor Menu</span>
                                    {expandedSections.sp ? <MdExpandMore /> : <MdChevronRight />}
                                </button>
                            )}
                            {(expandedSections.sp || !isOpen) && sponsorMenuItems.map((item, index) => (
                                <Link
                                    key={index}
                                    to={item.path}
                                    className={`${styles.navItem} ${location.pathname === item.path ? styles.active : ''}`}
                                    title={!isOpen ? item.label : ''}
                                >
                                    <item.icon className={styles.navIcon} />
                                    {isOpen && <span className={styles.navLabel}>{item.label}</span>}
                                </Link>
                            ))}
                        </div>
                    )}
                </nav>

                {/* User Section */}
                <div className={styles.userSection}>
                    <div className={styles.userInfo}>
                        <div className={styles.avatar}>
                            {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        {isOpen && (
                            <div className={styles.userDetails}>
                                <div className={styles.userName}>{user?.fullName || 'User'}</div>
                                <div className={styles.userRole}>
                                    {userRoleName || 'User'}
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        className={styles.signOutBtn}
                        onClick={handleLogoutClick}
                        title={!isOpen ? 'Sign Out' : ''}
                    >
                        <MdLogout className={styles.signOutIcon} />
                        {isOpen && <span>Sign Out</span>}
                    </button>
                </div>

                {/* Toggle Button */}
                <button
                    className={styles.toggleBtn}
                    onClick={onToggle}
                    title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                    <MdMenu />
                </button>
            </div>

            {/* Logout Confirmation Modal */}
            <ConfirmationModal
                show={showLogoutModal}
                title="Sign Out"
                message="Are you sure you want to sign out?"
                type="warning"
                confirmText="Sign Out"
                cancelText="Cancel"
                onConfirm={handleLogoutConfirm}
                onCancel={handleLogoutCancel}
            />
        </>
    );
};

export default Sidebar;
